/* eslint-disable consistent-return */
import React, { useState } from "react";
import { Tooltip } from "antd";
import { MdOutlineVpnKey } from "react-icons/md";
import * as S from "./styles";
import { ChangeCardPasswordModal } from "../../ChangeCardPasswordModal";
import { AuthorizeChangeCardPasswordModal } from "../../AuthorizeChangeCardPasswordModal";
import { ModalChangePasswordChannel } from "../ModalChangePasswordChannel";
import { usePermissions } from "../../../Context/permissionContext";
import { CompanyParams as useCompanyParams } from "../../../Context/companyContext";
import { useClient } from "../../../hooks/ClientProvider";
import { hasPushPermission } from "../../../helpers/hasPushPermission";
import api from "../../../services/api";
import { isCustomError } from "../../../utils/validations";
import { notifyError, notifySucces } from "../../../utils/notifications";

type ChangeCardPasswordActionProps = {
  productId: number;
  cardId: number;
  clientId: number;
  lastFour: string;
  pushClientId?: number;
  pushClientCpf?: string;
};

export function ChangeCardPasswordAction({
  lastFour,
  cardId,
  clientId,
  productId,
  pushClientId,
  pushClientCpf,
}: ChangeCardPasswordActionProps) {
  const { permissions } = usePermissions();
  const { companyParams } = useCompanyParams();
  const { client } = useClient();

  const [isChangeCardPasswordModalOpen, setIsChangeCardPasswordModalOpen] = useState(false);
  const [
    isAuthorizeChangeCardPasswordModalOpen,
    setIsAuthorizeChangeCardPasswordModalOpen,
  ] = useState(false);
  const [isChannelModalOpen, setIsChannelModalOpen] = useState(false);
  const [encryptedPassword, setEncryptedPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const hasPermissionToChangeCardPassword =
    permissions && permissions.includes("set_card_password");
  const hasPermissionToAuthorizeChangeCardPassword =
    permissions && permissions.includes("permission_authorize_change_card_password");
  const cardPasswordLength = companyParams?.card_password_length || 4;

  const clientCpf = pushClientCpf || client?.client?.cpf || "";

  const pushEnabled = hasPushPermission("redefinicao_senha", "push");
  const sacEnabled = hasPushPermission("redefinicao_senha", "sac");

  const isActionEnabled = Boolean( hasPermissionToChangeCardPassword && (pushEnabled || sacEnabled) );

  const toggleChangeCardPasswordModal = () => {
    if (!hasPermissionToChangeCardPassword) {
      return;
    }
    setIsChangeCardPasswordModalOpen((prevState) => !prevState);
  };

  const toggleAuthorizeChangeCardPasswordModal = () => {
    setIsAuthorizeChangeCardPasswordModalOpen((prevState) => !prevState);
  };

  const handleOpenPasswordResetFlow = () => {
      if (!isActionEnabled) return;
      setIsChannelModalOpen(true);
    };

  const handleChangeCardPassword = async (encryptedPwd: string) => {
    try {
      setIsLoading(true);
      const response = await api.patch("card_set_password", {
        card_id: cardId,
        cod_product: productId,
        client_id: clientId,
        new_password: encryptedPwd,
        lastFour,
      });

      const hasCustomError = isCustomError(response);
      const passwordChanged = response?.passwordChanged === true;

      if (hasCustomError || !passwordChanged) {
        return notifyError("Falha ao efetuar troca de senha do cartão", "", false, true);
      }

      notifySucces("Alteração de senha do cartão realizada com sucesso", true);
      setIsChangeCardPasswordModalOpen(false);
    } catch (e) {
      notifyError("Falha ao efetuar troca de senha do cartão", "", false, true);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitAuthorizeChangeCardPassword = async ({
    login,
    pwd: password,
  }: {
    login: string;
    pwd: string;
  }) => {
    try {
      setIsLoading(true);
      const response = await api.patch("card_set_password_authorization", {
        card_id: cardId,
        cod_product: productId,
        client_id: clientId,
        login,
        password,
        new_password: encryptedPassword,
        lastFour,
      });

      const hasCustomError = isCustomError(response);
      const passwordChanged = response?.passwordChanged === true;

      if (hasCustomError || !passwordChanged) {
        notifyError("Falha ao efetuar troca de senha do cartão", "", false, true);
        return { success: false };
      }

      notifySucces("Alteração de senha do cartão realizada com sucesso", true);
      setIsAuthorizeChangeCardPasswordModalOpen(false);
      return { success: true };
    } catch (e) {
      notifyError("Falha ao efetuar troca de senha do cartão", "", false, true);
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitChangeCardPassword = (encryptedPwd: string) => {
    if (
      !companyParams?.has_change_card_password_confirmation ||
      hasPermissionToAuthorizeChangeCardPassword
    ) {
      return handleChangeCardPassword(encryptedPwd);
    }
    setEncryptedPassword(encryptedPwd);
    toggleChangeCardPasswordModal();
    toggleAuthorizeChangeCardPasswordModal();
  };

  return (
    <>
      <Tooltip title="Alterar senha do cartão">
        <S.MenuItem
          onClick={handleOpenPasswordResetFlow}
          disabled={!isActionEnabled}
          data-testid="botao_alterar_senha_cartao"
        >
          <MdOutlineVpnKey className="icon" /> Alterar senha
        </S.MenuItem>
      </Tooltip>

      <ModalChangePasswordChannel
        open={isChannelModalOpen}
        closeModal={() => setIsChannelModalOpen(false)}
        clientId={clientId}
        additionalClientId={pushClientId}
        cardId={cardId}
        clientCpf={clientCpf}
        pushEnabled={pushEnabled}
        sacEnabled={sacEnabled}
        onSelectSacFlow={() => setIsChangeCardPasswordModalOpen(true)}
      />

      <ChangeCardPasswordModal
        isOpen={isChangeCardPasswordModalOpen}
        onClose={toggleChangeCardPasswordModal}
        onSubmitChangeCardPassword={onSubmitChangeCardPassword}
        isSubmitting={isLoading}
        cardPasswordLength={cardPasswordLength}
      />

      <AuthorizeChangeCardPasswordModal
        onClose={toggleAuthorizeChangeCardPasswordModal}
        isOpen={isAuthorizeChangeCardPasswordModalOpen}
        onSubmit={onSubmitAuthorizeChangeCardPassword}
        isSubmitting={isLoading}
      />
    </>
  );
}